import type { IconName } from '@fortawesome/fontawesome-svg-core';
import {
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { Palette } from '@mui/material/styles';
import type { FormikHelpers } from 'formik';
import { Form, Formik } from 'formik';
import { navigate } from 'gatsby';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import type { Flags, Schema } from 'yup';
import { array, boolean, object, string } from 'yup';

import RecordAttachmentForm from '@components/Attachments/RecordAttachmentForm';
import {
  FormikDropdownField,
  FormikFhirDateField,
  FormikTextField,
} from '@components/FormikFields';
import { RecordSubmitButton } from '@components/RecordSubmitButton';
import RelatedRecordsForm from '@components/RelatedRecordsTable/RelatedRecordsForm';

import {
  ALLERGY_REACTION_OPTIONS,
  ALLERGY_TYPE_OPTIONS,
  CLINICAL_STATUS_OPTIONS,
  NEGATED_ALLERGY_OPTIONS,
} from '@constants/option-types/allergy';
import * as ROUTES from '@constants/routes';

import { useCreateAllergy, useUpdateAllergy } from '@hooks/api/allergy';
import useFhirRecordForm from '@hooks/useFhirRecordForm';
import { useFormat } from '@hooks/useFormat';
import { useSnackbar } from '@hooks/useSnackbar';

import { HorizontalRule } from '@layouts/AuthLayout';
import { DrilldownItem } from '@layouts/DrilldownStyle';

import type { AllergyRecord, AllergyValues, FormValues } from '@models/allergy';
import { getAllergyTemplate } from '@models/fhir-templates/allergy';
import type { DrilldownProps } from '@models/record-drilldown';

import { capitalize } from '@utils/format';
import { getRecordSourceForSubmit } from '@utils/getRecordSourceForSubmit';
import { stripEmptyStrings } from '@utils/strip';

import SharingDrilldownWrapper from '@views/Records/Common/SharingDrilldownWrapper';

import DrilldownWrapper from '../Common/DrilldownWrapper';

const messages = defineMessages({
  lastKnown: {
    id: 'allergyPageDrilldown.lastKnown',
    defaultMessage: 'Last known reaction',
  },
  response: {
    id: 'allergyPageDrilldown.response',
    defaultMessage: 'Response',
  },
  description: {
    id: 'allergyPageDrilldown.description',
    defaultMessage: 'Description',
  },
  clinicalStatus: {
    id: 'vitalSigns.clinicalStatus',
    defaultMessage: 'Clinical Status',
  },
  allergyType: {
    id: 'allergyPageDrilldown.allergyType',
    defaultMessage: 'Allergy Type',
  },
  title: {
    id: 'allergyPageDrilldown.title',
    defaultMessage: 'Allergy',
  },
  required: {
    id: 'GLOBAL.required',
    defaultMessage: 'This field is required',
  },
  error: {
    id: 'GLOBAL.error',
    defaultMessage: 'An error occurred. Please try again later.',
  },
  allergen: {
    id: 'allergyPageDrilldown.allergen',
    defaultMessage: 'Allergen',
  },
  severity: {
    id: 'allergyPageDrilldown.severity',
    defaultMessage: 'Reaction Severity',
  },
  negatedAllergies: {
    id: 'allergyPageDrilldown.negatedAllergies',
    defaultMessage: 'Negated Allergies',
  },
  date: {
    id: 'GLOBAL.date',
    defaultMessage: 'Date',
  },
});

// Get grade color
function getGradeIconConfig(
  grade: string | undefined,
  palette: Palette
): {
  iconName: IconName;
  iconColor: string;
} {
  switch (grade) {
    case 'mild':
      return { iconName: 'circle-small', iconColor: palette.warning.main };
    case 'severe':
      return { iconName: 'circle-small', iconColor: palette.error.main };
    case 'moderate':
    default:
      return { iconName: 'circle-small', iconColor: palette.warning.dark };
  }
}

interface AllergyDrilldownProps extends DrilldownProps<AllergyRecord> {
  /**
   * When true, suppresses the default redirect to the ALLERGIES route
   * after successfully creating a new allergy.
   * - Default: false (keeps existing behavior in normal routes)
   * - In auto-curation flow, this should be set to true.
   */
  disableNavigateOnCreate?: boolean;
}

const Allergy: React.FC<AllergyDrilldownProps> = ({
  data,
  loading: baseLoading,
  isSharing = false,
  onSubmit,
  isCreating,
  isEditing,
  patientId: patient_id,
  isCurator,
  disableNavigateOnCreate = false,
}) => {
  const { formatMessage } = useIntl();
  const { formatDateLabel } = useFormat();
  const { enqueueSnackbar } = useSnackbar();
  const {
    formikHelpers: { baseFhirValidationSchema, initialFhirFormValues },
    loading: recordStatusAndSourceLoading,
    handleRecordFilesSourcePublishing,
  } = useFhirRecordForm();
  const isModifying = isCreating || isEditing;

  const [createAllergy, createAllergyLoading] = useCreateAllergy();
  const [updateAllergy, updateAllergyLoading] = useUpdateAllergy();

  const { palette } = useTheme();

  const loading =
    baseLoading ||
    updateAllergyLoading ||
    createAllergyLoading ||
    recordStatusAndSourceLoading;

  const initialValues: FormValues = {
    ...initialFhirFormValues,
    title: data?.substance ?? '',
    lastKnownReaction: data?.lastKnownReaction ?? '',
    reactionDescription: data?.reactionDescription ?? '',
    responseDescription: data?.responseDescription ?? '',
    allergyType: data?.allergyType ?? '',
    clinicalStatus: data?.clinicalStatus ?? '',
    selfReported: data?.selfReported ?? false,
    severity: data?.severity ?? '',
    noAllergies: data?.noAllergies ?? '',
    associatedResources: [],
  };

  const allergyValidationSchema: Schema<Partial<AllergyValues>> =
    object().shape({
      title: string().when('noAllergies', {
        is: (value: string | null | undefined) => Boolean(value),
        then: () => string(),
        otherwise: () => string().required(formatMessage(messages.required)),
      }),
      lastKnownReaction: string(),
      reactionDescription: string(),
      responseDescription: string(),
      allergyType: string(),
      clinicalStatus: string().required(formatMessage(messages.required)),
      selfReported: boolean().required(formatMessage(messages.required)),
      noAllergies: string(),
      severity: string(),
      associatedResources: array().of(
        string().required(formatMessage(messages.required))
      ),
    });

  const validationSchema: Schema<
    Partial<FormValues>,
    any,
    any,
    Flags
  > = allergyValidationSchema.concat(baseFhirValidationSchema);

  async function handleSubmit(
    values: FormValues,
    { validateForm }: FormikHelpers<FormValues>
  ) {
    if (!patient_id) return;

    values = stripEmptyStrings(values);
    await validateForm(stripEmptyStrings(values));

    let allergy: AllergyRecord | null | undefined;
    try {
      if (data?.id) {
        const { data: updatedAllergy } = await updateAllergy({
          input: {
            allergy_id: data.id,
            allergy: data.getModelInput(values),
          },
        });
        allergy = updatedAllergy;
      } else {
        const { data } = await createAllergy({
          input: {
            ...getAllergyTemplate(patient_id, values),
          },
        });
        allergy = data;
      }
    } catch (e) {
      enqueueSnackbar({
        variant: 'error',
        message: formatMessage(messages.error),
      });
    } finally {
      await handleRecordFilesSourcePublishing({
        resource: allergy?.getResourceId(),
        files: values.files,
        toPublish: values.toPublish,
        recordSource: getRecordSourceForSubmit({
          isCurator,
          isCreating,
          initialRecordSource: initialValues.recordSource,
          currentRecordSource: values.recordSource,
        }),
      });
      // Existing behavior preserved by default.
      // In auto-curation flow, disableNavigateOnCreate=true will skip this.
      if (allergy && isCreating && !disableNavigateOnCreate) {
        navigate(`${ROUTES.ALLERGIES}${allergy.id}`);
      }
      onSubmit?.();
    }
  }

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
    >
      {({ setFieldValue, values }) => {
        const reactionGradeIconConfig = getGradeIconConfig(
          (isModifying ? values.severity : data?.severity) ?? undefined,
          palette
        );

        const children = (
          <>
            {!values.noAllergies && (
              <>
                <DrilldownItem
                  label={formatMessage(messages.description)}
                  name="reactionDescription"
                  value={data?.reactionDescription}
                />

                <DrilldownItem
                  label={formatMessage(messages.response)}
                  name="responseDescription"
                  value={data?.responseDescription}
                />

                <DrilldownItem
                  label={formatMessage(messages.allergyType)}
                  name="allergyType"
                  options={ALLERGY_TYPE_OPTIONS}
                  type="dropdown"
                  value={data?.allergyType ? capitalize(data.allergyType) : '-'}
                />
              </>
            )}

            <DrilldownItem
              label={formatMessage(messages.clinicalStatus)}
              name="clinicalStatus"
              options={CLINICAL_STATUS_OPTIONS}
              required
              type="dropdown"
              value={data?.clinicalStatus}
            />

            <RelatedRecordsForm associations={values.associatedResources} />

            <RecordAttachmentForm
              isCreating={isCreating}
              onFileChange={(files: DroppedFile[]) => {
                setFieldValue('files', files);
              }}
              patientId={patient_id}
              record={{
                attachments: data?.attachments,
                resourceId: data?.getResourceId(),
              }}
            />

            {isModifying && (
              <>
                <HorizontalRule />
                <RecordSubmitButton />
              </>
            )}
          </>
        );
        const drilldownProps = {
          items: values.noAllergies
            ? undefined
            : [
                {
                  editingComponent: (
                    <FormikDropdownField
                      label={formatMessage(messages.severity)}
                      labelColor="primary.contrastText"
                      name="severity"
                      options={ALLERGY_REACTION_OPTIONS}
                    />
                  ),
                  title: `${formatMessage(
                    messages.severity
                  )}: ${data?.reactionGrade}`,
                  ...reactionGradeIconConfig,
                },
              ],
          loading,
          resource: data,
          selfReported: data?.selfReported,
          subtitle: isModifying ? (
            <FormikFhirDateField
              formats={['date', 'year']}
              label={formatMessage(
                values.noAllergies ? messages.date : messages.lastKnown
              )}
              name="lastKnownReaction"
              onChange={(value) => {
                setFieldValue('lastKnownReaction', value);
              }}
              max={true}
              value={data?.lastKnownReaction}
            />
          ) : (
            formatDateLabel(messages.lastKnown, data?.getLastKnownReaction())
          ),
          title: data?.substance || formatMessage(messages.title),
          titleInput: (
            <Stack spacing={2}>
              <>
                <FormLabel
                  sx={{ color: palette.primary.contrastText, fontSize: 18 }}
                >
                  {formatMessage(messages.negatedAllergies)}
                </FormLabel>
                <RadioGroup
                  defaultValue={values.noAllergies}
                  name="noAllergies"
                  onChange={(event) =>
                    setFieldValue('noAllergies', event.target.value)
                  }
                  value={values.noAllergies}
                >
                  {NEGATED_ALLERGY_OPTIONS.map(({ k, v }) => {
                    return (
                      <FormControlLabel
                        control={
                          <Radio
                            sx={{
                              color: palette.primary.contrastText,
                              '&.Mui-checked': {
                                color: palette.primary.contrastText,
                              },
                            }}
                          />
                        }
                        key={k}
                        label={v}
                        value={k}
                      />
                    );
                  })}
                </RadioGroup>
              </>
              {!values.noAllergies && (
                <FormikTextField
                  name="title"
                  placeholder={formatMessage(messages.allergen)}
                />
              )}
            </Stack>
          ),
        };

        return (
          <Form>
            {isSharing ? (
              <SharingDrilldownWrapper {...drilldownProps}>
                {children}
              </SharingDrilldownWrapper>
            ) : (
              <DrilldownWrapper
                associatedRecords={data?.associatedRecords}
                backRoute={ROUTES.ALLERGIES}
                notes={data?.recordNotes}
                {...drilldownProps}
              >
                {children}
              </DrilldownWrapper>
            )}
          </Form>
        );
      }}
    </Formik>
  );
};

export default Allergy;
